import type { Poll, PollResult, PollVote } from '@vc/models/poll'

/**
 * The numbers the results dialog shows for one poll.
 *
 * Participants are the people who *can* vote: everyone in the room except the
 * poll's creator. The server refuses the creator's own vote (`'own_poll'` in
 * `poll.service.ts`), so counting them meant a room of host + 4 read "5
 * participants" and topped out at 80% when every one of the 4 had voted. The
 * creator is only subtracted while they are actually here — a poll whose author
 * has left is answered by everyone still present. A poll without `createdById`
 * (older backend) assumes its author is present, which is the common case.
 *
 * The rate comes from `poll.totalVotes`, the server's count of people who
 * voted. It used to be the length of `pollVotes`, which only ever holds this
 * browser's own vote — the host saw 0% however many had voted, each voter saw
 * one vote's worth, and no two people saw the same figure.
 *
 * Capped at 100: someone who voted and then left still counts in `totalVotes`
 * but no longer in the room.
 */
export function pollResultFor(
  poll: Poll,
  votes: PollVote[],
  presentParticipantIds: readonly string[],
): PollResult {
  const creatorPresent = poll.createdById
    ? presentParticipantIds.includes(poll.createdById)
    : true
  const totalParticipants = Math.max(0, presentParticipantIds.length - (creatorPresent ? 1 : 0))
  const participationRate =
    totalParticipants > 0 ? Math.min(100, (poll.totalVotes / totalParticipants) * 100) : 0
  return {
    poll,
    votes: votes.filter((v) => v.pollId === poll.id),
    totalParticipants,
    participationRate,
  }
}
